import { Input } from '@/shared/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select';
import { InteractionType, FeedbackStatus } from '@momentum/types';
import { useTranslation } from 'react-i18next';
import type { FilterState } from '../table/types/interaction-table.types';

interface InteractionsFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
}

export const InteractionsFilters = ({
  filters,
  onFiltersChange,
}: InteractionsFiltersProps) => {
  const { t } = useTranslation('interactions');

  return (
    <div className="bg-card rounded-lg border p-6 shadow-sm">
      <h3 className="text-sm font-semibold mb-4 text-foreground">
        {t('filters')}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div>
          <label className="text-xs font-medium mb-2 block text-muted-foreground uppercase tracking-wide">
            {t('search')}
          </label>
          <Input
            placeholder={t('searchByParticipant')}
            value={filters.search}
            onChange={(e) =>
              onFiltersChange({ ...filters, search: e.target.value })
            }
            className="h-10"
          />
        </div>
        <div>
          <label className="text-xs font-medium mb-2 block text-muted-foreground uppercase tracking-wide">
            {t('type')}
          </label>
          <Select
            value={filters.type}
            onValueChange={(value) =>
              onFiltersChange({
                ...filters,
                type: value as InteractionType | 'all',
              })
            }
          >
            <SelectTrigger className="h-10">
              <SelectValue placeholder={t('allTypes')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allTypes')}</SelectItem>
              <SelectItem value={InteractionType.PRAISE}>
                {t('iSeeU')}
              </SelectItem>
              <SelectItem value={InteractionType.FEEDBACK}>
                {t('iNeedU')}
              </SelectItem>
              <SelectItem value={InteractionType.DEBRIEF}>
                {t('iLearn')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium mb-2 block text-muted-foreground uppercase tracking-wide">
            {t('status')}
          </label>
          <Select
            value={filters.status}
            onValueChange={(value) =>
              onFiltersChange({
                ...filters,
                status: value as FeedbackStatus | 'all',
              })
            }
          >
            <SelectTrigger className="h-10">
              <SelectValue placeholder={t('allStatuses')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('allStatuses')}</SelectItem>
              <SelectItem value={FeedbackStatus.SENT}>{t('sent')}</SelectItem>
              <SelectItem value={FeedbackStatus.ACTION_PLAN}>
                {t('actionPlan')}
              </SelectItem>
              <SelectItem value={FeedbackStatus.FINISHED}>
                {t('finished')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium mb-2 block text-muted-foreground uppercase tracking-wide">
            {t('fromDate')}
          </label>
          <Input
            type="date"
            value={filters.dateFrom}
            onChange={(e) =>
              onFiltersChange({ ...filters, dateFrom: e.target.value })
            }
            className="h-10"
          />
        </div>
        <div>
          <label className="text-xs font-medium mb-2 block text-muted-foreground uppercase tracking-wide">
            {t('toDate')}
          </label>
          <Input
            type="date"
            value={filters.dateTo}
            onChange={(e) =>
              onFiltersChange({ ...filters, dateTo: e.target.value })
            }
            className="h-10"
          />
        </div>
      </div>
    </div>
  );
};
